import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat, getBucketStringForTag} from './PerformanceUtils';
import {ProfilerEvent, ProfilerEventDomain, EventMetadata} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_PROCESS_EVENTS_VERSION = 5;

const NUM_NODES_BUCKET_CUTS = [0, 21, 51, 101, 501];

export enum ProfilerProcessesFeatures {
  CREATE_TASK_GROUP = 'createTaskGroup',
  EXECUTE_INTO_ENTRY = 'executeIntoEntry',
  DIRECT_EXECUTE = 'directExecute',
  SUBMIT_TASK_TABLE = 'submitTaskTable',
  FLOWCHART_CONFIG_INITIAL_LOAD = 'flowchartConfigInitialLoad',
  FLOWCHART_CONFIG_INITIAL_LOAD_DATA = 'flowchartConfigInitialLoadData',
  FLOWCHART_CONFIG_INITIAL_RENDER = 'flowchartConfigInitialRender',
  FLOWCHART_CONFIG_AUTOLAYOUT = 'flowchartConfigAutolayout',
  FLOWCHART_CONFIG_SAVE = 'flowchartConfigSave',
  FLOWCHART_CONFIG_CREATE = 'flowchartConfigCreate',
  FLOWCHART_CONFIG_ADD_NODE = 'flowchartConfigAddNode',
  FLOWCHART_CONFIG_DELETE_NODE = 'flowchartConfigDeleteNode',
  FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE = 'flowchartConfigTaskSchemaUpdate',
  FLOWCHART_CONFIG_TASK_SCHEMA_CREATE = 'flowchartConfigTaskSchemaCreate',
  FLOWCHART_CONFIG_VALIDATION = 'flowchartConfigValidation',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerProcessesFeatures]: Function} = {
  [ProfilerProcessesFeatures.CREATE_TASK_GROUP]: processCreateProcessAndTasks,
  [ProfilerProcessesFeatures.EXECUTE_INTO_ENTRY]: processExecuteIntoEntry,
  [ProfilerProcessesFeatures.DIRECT_EXECUTE]: processDirectExecute,
  [ProfilerProcessesFeatures.SUBMIT_TASK_TABLE]: processSubmitTaskTable,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_LOAD]: processFlowchartConfigInitialLoad,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_LOAD_DATA]: processFlowchartConfigInitialLoadData,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_RENDER]: processFlowchartConfigInitialRender,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_AUTOLAYOUT]: processFlowchartConfigAutolayout,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_SAVE]: processFlowchartConfigSave,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_CREATE]: processFlowchartConfigCreate,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_ADD_NODE]: processFlowchartConfigAddNode,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_DELETE_NODE]: processFlowchartConfigDeleteNode,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE]: processFlowchartConfigTaskSchemaUpdate,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_TASK_SCHEMA_CREATE]: processFlowchartConfigTaskSchemaCreate,
  [ProfilerProcessesFeatures.FLOWCHART_CONFIG_VALIDATION]: processFlowchartConfigValidation,
};

enum ProfilerProcessesActions {
  CREATE_TASK_GROUP_STARTED = 'createTaskGroupStarted',
  CREATE_TASK_GROUP_ENDED = 'createTaskGroupEnded',

  EXECUTE_INTO_ENTRY_STARTED = 'executeIntoEntryStarted',
  EXECUTE_INTO_ENTRY_ENDED = 'executeIntoEntryEnded',

  DIRECT_EXECUTE_STARTED = 'directExecuteStarted',
  DIRECT_EXECUTE_ENDED = 'directExecuteEnded',

  SUBMIT_TASK_TABLE_STARTED = 'submitTaskTableStarted',
  SUBMIT_TASK_TABLE_ENDED = 'submitTaskTableEnded',

  FLOWCHART_CONFIG_INITIAL_LOAD_STARTED = 'flowchartConfigInitialLoadStarted',
  FLOWCHART_CONFIG_INITIAL_LOAD_ENDED = 'flowchartConfigInitialLoadEnded',

  FLOWCHART_CONFIG_INITIAL_LOAD_DATA_STARTED = 'flowchartConfigInitialLoadDataStarted',
  FLOWCHART_CONFIG_INITIAL_LOAD_DATA_ENDED = 'flowchartConfigInitialLoadDataEnded',

  FLOWCHART_CONFIG_INITIAL_RENDER_STARTED = 'flowchartConfigInitialRenderStarted',
  FLOWCHART_CONFIG_INITIAL_RENDER_ENDED = 'flowchartConfigInitialRenderEnded',

  FLOWCHART_CONFIG_AUTOLAYOUT_STARTED = 'flowchartConfigAutolayoutStarted',
  FLOWCHART_CONFIG_AUTOLAYOUT_ENDED = 'flowchartConfigAutolayoutEnded',

  FLOWCHART_CONFIG_SAVE_STARTED = 'flowchartConfigSaveStarted',
  FLOWCHART_CONFIG_SAVE_ENDED = 'flowchartConfigSaveEnded',

  FLOWCHART_CONFIG_CREATE_STARTED = 'flowchartConfigCreateStarted',
  FLOWCHART_CONFIG_CREATE_ENDED = 'flowchartConfigCreateEnded',

  FLOWCHART_CONFIG_ADD_NODE_STARTED = 'flowchartConfigAddNodeStarted',
  FLOWCHART_CONFIG_ADD_NODE_ENDED = 'flowchartConfigAddNodeEnded',

  FLOWCHART_CONFIG_DELETE_NODE_STARTED = 'flowchartConfigDeleteNodeStarted',
  FLOWCHART_CONFIG_DELETE_NODE_ENDED = 'flowchartConfigDeleteNodeEnded',

  FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_STARTED = 'flowchartConfigTaskSchemaUpdateStarted',
  FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_ENDED = 'flowchartConfigTaskSchemaUpdateEnded',

  FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_STARTED = 'flowchartConfigTaskSchemaCreateStarted',
  FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_ENDED = 'flowchartConfigTaskSchemaCreateEnded',

  FLOWCHART_CONFIG_VALIDATION_STARTED = 'flowchartConfigValidationStarted',
  FLOWCHART_CONFIG_VALIDATION_ENDED = 'flowchartConfigValidationEnded',
}

export const PROCESSES_CREATE_TASK_GROUP_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.CREATE_TASK_GROUP,
  action: ProfilerProcessesActions.CREATE_TASK_GROUP_STARTED,
};

export const PROCESSES_CREATE_TASK_GROUP_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.CREATE_TASK_GROUP,
  action: ProfilerProcessesActions.CREATE_TASK_GROUP_ENDED,
};

export const PROCESSES_EXECUTE_INTO_ENTRY_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.EXECUTE_INTO_ENTRY,
  action: ProfilerProcessesActions.EXECUTE_INTO_ENTRY_STARTED,
};

export const PROCESSES_EXECUTE_INTO_ENTRY_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.EXECUTE_INTO_ENTRY,
  action: ProfilerProcessesActions.EXECUTE_INTO_ENTRY_ENDED,
};

export const PROCESSES_DIRECT_EXECUTE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.DIRECT_EXECUTE,
  action: ProfilerProcessesActions.DIRECT_EXECUTE_STARTED,
};

export const PROCESSES_DIRECT_EXECUTE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.DIRECT_EXECUTE,
  action: ProfilerProcessesActions.DIRECT_EXECUTE_ENDED,
};

export const PROCESSES_SUBMIT_TASK_TABLE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.SUBMIT_TASK_TABLE,
  action: ProfilerProcessesActions.SUBMIT_TASK_TABLE_STARTED,
};

export const PROCESSES_SUBMIT_TASK_TABLE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.SUBMIT_TASK_TABLE,
  action: ProfilerProcessesActions.SUBMIT_TASK_TABLE_ENDED,
};

export const FLOWCHART_CONFIG_INITIAL_LOAD_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_LOAD,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_INITIAL_LOAD_STARTED,
};
export const FLOWCHART_CONFIG_INITIAL_LOAD_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_LOAD,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_INITIAL_LOAD_ENDED,
};

export const FLOWCHART_CONFIG_INITIAL_LOAD_DATA_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_LOAD_DATA,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_INITIAL_LOAD_DATA_STARTED,
};
export const FLOWCHART_CONFIG_INITIAL_LOAD_DATA_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_LOAD_DATA,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_INITIAL_LOAD_DATA_ENDED,
};

export const FLOWCHART_CONFIG_INITIAL_RENDER_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_RENDER,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_INITIAL_RENDER_STARTED,
};
export const FLOWCHART_CONFIG_INITIAL_RENDER_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_INITIAL_RENDER,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_INITIAL_RENDER_ENDED,
};

export const FLOWCHART_CONFIG_AUTOLAYOUT_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_AUTOLAYOUT,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_AUTOLAYOUT_STARTED,
};
export const FLOWCHART_CONFIG_AUTOLAYOUT_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_AUTOLAYOUT,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_AUTOLAYOUT_ENDED,
};

export const FLOWCHART_CONFIG_SAVE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_SAVE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_SAVE_STARTED,
};
export const FLOWCHART_CONFIG_SAVE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_SAVE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_SAVE_ENDED,
};

export const FLOWCHART_CONFIG_CREATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_CREATE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_CREATE_STARTED,
};
export const FLOWCHART_CONFIG_CREATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_CREATE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_CREATE_ENDED,
};

export const FLOWCHART_CONFIG_ADD_NODE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_ADD_NODE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_ADD_NODE_STARTED,
};
export const FLOWCHART_CONFIG_ADD_NODE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_ADD_NODE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_ADD_NODE_ENDED,
};

export const FLOWCHART_CONFIG_DELETE_NODE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_DELETE_NODE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_DELETE_NODE_STARTED,
};
export const FLOWCHART_CONFIG_DELETE_NODE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_DELETE_NODE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_DELETE_NODE_ENDED,
};

export const FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_STARTED,
};
export const FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_ENDED,
};

export const FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_TASK_SCHEMA_CREATE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_STARTED,
};
export const FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_TASK_SCHEMA_CREATE,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_ENDED,
};

export const FLOWCHART_CONFIG_VALIDATION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_VALIDATION,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_VALIDATION_STARTED,
};
export const FLOWCHART_CONFIG_VALIDATION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.FLOWCHART_CONFIG_VALIDATION,
  action: ProfilerProcessesActions.FLOWCHART_CONFIG_VALIDATION_ENDED,
};

export function processProcessesEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerProcessesFeatures;
  assert(
    Object.values(ProfilerProcessesFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

export function getSizeBucket(num: number): string {
  if (num === 0) {
    return 'eq_0';
  } else if (num <= 100) {
    return '1_to_100';
  } else if (num <= 500) {
    return '100_to_500';
  } else if (num <= 1000) {
    return '500_to_1000';
  } else {
    return 'gt_1000';
  }
}

interface ProcessEventMetadataOptions {
  numItems: number;
  actionOrigin?: ActionOrigin;
  tableLocation?: string;
  numTasks?: number;
  numOutputs?: number;
}

export function createProcessEventMetadata({
  numItems,
  actionOrigin,
  tableLocation,
  numTasks,
  numOutputs,
}: ProcessEventMetadataOptions): EventMetadata {
  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations
  const metadata: EventMetadata = {
    distributionMetadata: {
      size_bucket: getSizeBucket(numItems),
    },
    actionMetadata: {
      size_bucket: getSizeBucket(numItems),
      num_items: numItems,
    },
  };

  if (numTasks != null) {
    metadata.actionMetadata.num_tasks = numTasks;
  }

  if (numOutputs != null) {
    metadata.actionMetadata.num_outputs = numOutputs;
  }

  if (actionOrigin != null) {
    // For perf purposes we can consider any action origin from the sidepanel (search dashboard)
    // to be the same
    let coercedActionOrigin: string = actionOrigin;
    if (
      actionOrigin === ActionOrigin.SIDEPANEL_GLOBAL_CREATE ||
      actionOrigin === ActionOrigin.WORKFLOWS_SIDEPANEL_CREATE
    ) {
      coercedActionOrigin = 'SIDEPANEL';
    }

    coercedActionOrigin = toDatadogTagFormat(coercedActionOrigin);

    metadata.distributionMetadata.action_origin = coercedActionOrigin;
    metadata.actionMetadata.action_origin = coercedActionOrigin;
  }
  if (tableLocation != null) {
    const coercedTableLocation = toDatadogTagFormat(tableLocation);
    metadata.distributionMetadata.table_location = coercedTableLocation;
    metadata.actionMetadata.table_location = coercedTableLocation;
  }

  return metadata;
}

/**
 * Process a processes (workflows) generate analysis event
 */
function processCreateProcessAndTasks(): void {
  processTimingEvent(
    PROCESSES_CREATE_TASK_GROUP_STARTED_EVENT,
    PROCESSES_CREATE_TASK_GROUP_ENDED_EVENT,
    'Workflows Create Task Group with Tasks',
    'perf.process_create_task_group_with_tasks',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processExecuteIntoEntry(): void {
  processTimingEvent(
    PROCESSES_EXECUTE_INTO_ENTRY_STARTED_EVENT,
    PROCESSES_EXECUTE_INTO_ENTRY_ENDED_EVENT,
    'Workflows Execute Into Entry',
    'perf.process_execute_into_entry',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processDirectExecute(): void {
  processTimingEvent(
    PROCESSES_DIRECT_EXECUTE_STARTED_EVENT,
    PROCESSES_DIRECT_EXECUTE_ENDED_EVENT,
    'Workflows Direct Execute',
    'perf.process_direct_execute',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processSubmitTaskTable(): void {
  processTimingEvent(
    PROCESSES_SUBMIT_TASK_TABLE_STARTED_EVENT,
    PROCESSES_SUBMIT_TASK_TABLE_ENDED_EVENT,
    'Workflows Submit Task Table',
    'perf.process_submit_task_table',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

export function composeFlowchartEventMetadata({
  flowchartType,
  numNodes,
  numControlFlowEdges,
  numDataFlowEdges,
  editorMode,
  isBlocking,
  flowchartConfigId,
  wasHiddenDuringRender,
}: {
  flowchartType: string;
  numNodes: number;
  numControlFlowEdges: number;
  numDataFlowEdges: number;
  editorMode?: string;
  isBlocking?: boolean;
  flowchartConfigId?: string;
  wasHiddenDuringRender?: boolean;
}): EventMetadata {
  const formattedFlowchartType = toDatadogTagFormat(flowchartType);
  const numEdges = numControlFlowEdges + numDataFlowEdges;
  const metadata: EventMetadata = {
    distributionMetadata: {
      flowchart_type: formattedFlowchartType,
      num_nodes: getBucketStringForTag(numNodes, NUM_NODES_BUCKET_CUTS),
    },
    actionMetadata: {
      flowchart_type: formattedFlowchartType,
      num_nodes: numNodes,
      num_edges: numEdges,
      num_control_flow_edges: numControlFlowEdges,
      num_data_flow_edges: numDataFlowEdges,
    },
  };

  if (editorMode != null) {
    const formattedEditorMode = toDatadogTagFormat(editorMode);
    metadata.distributionMetadata.editor_mode = formattedEditorMode;
    metadata.actionMetadata.editor_mode = formattedEditorMode;
  }

  if (isBlocking != null) {
    metadata.distributionMetadata.is_blocking = isBlocking;
    metadata.actionMetadata.is_blocking = isBlocking;
  }

  if (flowchartConfigId != null) {
    metadata.actionMetadata.flowchart_config_id = toDatadogTagFormat(flowchartConfigId, {
      removeInvalidCharacters: true,
    });
  }

  if (wasHiddenDuringRender != null) {
    metadata.distributionMetadata.was_hidden = wasHiddenDuringRender;
    metadata.actionMetadata.was_hidden = wasHiddenDuringRender;
  }

  return metadata;
}

export function composeFlowchartCreateEventMetadata({
  flowchartType,
  hasOutputs,
}: {
  flowchartType: string;
  hasOutputs: boolean;
}): EventMetadata {
  const formattedFlowchartType = toDatadogTagFormat(flowchartType);
  return {
    distributionMetadata: {
      flowchart_type: formattedFlowchartType,
      has_outputs: hasOutputs,
    },
    actionMetadata: {
      flowchart_type: formattedFlowchartType,
      has_outputs: hasOutputs,
    },
  };
}

function processFlowchartConfigInitialLoad(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_INITIAL_LOAD_STARTED_EVENT,
    FLOWCHART_CONFIG_INITIAL_LOAD_ENDED_EVENT,
    'Flowchart Config Initial Load',
    'perf.flowchart_config_initial_load',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigInitialLoadData(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_INITIAL_LOAD_DATA_STARTED_EVENT,
    FLOWCHART_CONFIG_INITIAL_LOAD_DATA_ENDED_EVENT,
    'Flowchart Config Initial Load Data',
    'perf.flowchart_config_initial_load_data',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigInitialRender(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_INITIAL_RENDER_STARTED_EVENT,
    FLOWCHART_CONFIG_INITIAL_RENDER_ENDED_EVENT,
    'Flowchart Config Initial Render',
    'perf.flowchart_config_initial_render',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigAutolayout(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_AUTOLAYOUT_STARTED_EVENT,
    FLOWCHART_CONFIG_AUTOLAYOUT_ENDED_EVENT,
    'Flowchart Config Autolayout',
    'perf.flowchart_config_autolayout',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigSave(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_SAVE_STARTED_EVENT,
    FLOWCHART_CONFIG_SAVE_ENDED_EVENT,
    'Flowchart Config Save',
    'perf.flowchart_config_save',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigCreate(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_CREATE_STARTED_EVENT,
    FLOWCHART_CONFIG_CREATE_ENDED_EVENT,
    'Flowchart Config Create',
    'perf.flowchart_config_create',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigAddNode(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_ADD_NODE_STARTED_EVENT,
    FLOWCHART_CONFIG_ADD_NODE_ENDED_EVENT,
    'Flowchart Config Add Node',
    'perf.flowchart_config_add_node',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigDeleteNode(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_DELETE_NODE_STARTED_EVENT,
    FLOWCHART_CONFIG_DELETE_NODE_ENDED_EVENT,
    'Flowchart Config Delete Node',
    'perf.flowchart_config_delete_node',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

export function composeFlowchartTaskSchemaEventMetadata({
  flowchartType,
  executionType,
  flowchartConfigId,
}: {
  flowchartType: string;
  executionType: string;
  flowchartConfigId?: string;
}): EventMetadata {
  const formattedFlowchartType = toDatadogTagFormat(flowchartType);
  const formattedExecutionType = toDatadogTagFormat(executionType);
  const metadata: EventMetadata = {
    distributionMetadata: {
      flowchart_type: formattedFlowchartType,
      execution_type: formattedExecutionType,
    },
    actionMetadata: {
      flowchart_type: formattedFlowchartType,
      execution_type: formattedExecutionType,
    },
  };

  if (flowchartConfigId != null) {
    metadata.actionMetadata.flowchart_config_id = toDatadogTagFormat(flowchartConfigId, {
      removeInvalidCharacters: true,
    });
  }

  return metadata;
}

function processFlowchartConfigTaskSchemaUpdate(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_STARTED_EVENT,
    FLOWCHART_CONFIG_TASK_SCHEMA_UPDATE_ENDED_EVENT,
    'Flowchart Config Task Schema Update',
    'perf.flowchart_config_task_schema_update',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigTaskSchemaCreate(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_STARTED_EVENT,
    FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_ENDED_EVENT,
    'Flowchart Config Task Schema Create',
    'perf.flowchart_config_task_schema_create',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processFlowchartConfigValidation(): void {
  processTimingEvent(
    FLOWCHART_CONFIG_VALIDATION_STARTED_EVENT,
    FLOWCHART_CONFIG_VALIDATION_ENDED_EVENT,
    'Flowchart Config Validation',
    'perf.flowchart_config_validation',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}
